import { Controller } from "@hotwired/stimulus"
import { fillAssistantComposer } from "../utils/assistant_composer"
import { trackSuggestionEvent } from "../utils/assistant_suggestion_tracking"

/**
 * Suggestion Fill Controller
 *
 * Sets predefined text into the assistant input field when a suggestion is clicked.
* Also automatically populates the input field when the page loads with the
* prompt URL parameter (assistant_input_text is still accepted for backwards compatibility).
 *
 * Params:
 * - text: String text to insert into the input
 * - specialistSlug: Optional specialist slug to set hidden field
 *
 * URL Parameters:
 * - prompt: URL-encoded text to automatically populate on page load
 * - assistant_input_text: deprecated alias for backward compatibility
 *
 * Example:
 * <div data-controller="suggestion-fill">
 *   <div
 *     data-action="click->suggestion-fill#apply"
 *     data-suggestion-fill-text-param="Write a project brief"
 *   >Suggestion</div>
 * </div>
 * 
* URL Example:
* /asistente?prompt=Redacta+una+versión+personalizada...
 */
export default class extends Controller {
  connect() {
    // Check for prompt URL parameter on page load
    this._handleUrlParameter()
  }

  apply(event) {
    const text = event.params.text || ""
    const specialistSlug = event.params.specialistSlug || ""
    const source = event.params.source || ""

    trackSuggestionEvent("clicked", source)
    this._populateInput(text, specialistSlug, source)
  }

  // Private: Handle URL parameter for prompt / assistant_input_text
  _handleUrlParameter() {
    const urlParams = new URLSearchParams(window.location.search)
    const promptParam = urlParams.get('prompt') || urlParams.get('assistant_input_text')
    
    if (!promptParam) return
    
    // Safely decode the URL-encoded text with comprehensive error handling
    let decodedText = this._safeDecodeURIComponent(promptParam)
    
    // Use the same logic as the apply method to populate the input
    this._populateInput(decodedText, '', '')
  }

  // Private: Safely decode URI component with multiple fallback strategies
  _safeDecodeURIComponent(encodedString) {
    // Strategy 1: Direct decode
    try {
      return decodeURIComponent(encodedString)
    } catch (error) {
      console.warn('Direct decode failed, trying fallback strategies:', error)
    }

    // Strategy 2: Replace + with spaces and try again
    try {
      const withSpaces = encodedString.replace(/\+/g, ' ')
      return decodeURIComponent(withSpaces)
    } catch (error) {
      console.warn('Space replacement decode failed:', error)
    }

    // Strategy 3: Manual character replacement for common issues
    try {
      let cleaned = encodedString
        .replace(/\+/g, ' ')
        .replace(/%0A/g, '\n')
        .replace(/%0D/g, '\r')
        .replace(/%09/g, '\t')
        .replace(/%20/g, ' ')
        .replace(/%22/g, '"')
        .replace(/%27/g, "'")
        .replace(/%3C/g, '<')
        .replace(/%3E/g, '>')
        .replace(/%26/g, '&')
        .replace(/%3D/g, '=')
        .replace(/%2B/g, '+')
        .replace(/%2F/g, '/')
        .replace(/%3F/g, '?')
        .replace(/%23/g, '#')
        .replace(/%25/g, '%')
      
      return decodeURIComponent(cleaned)
    } catch (error) {
      console.warn('Manual character replacement decode failed:', error)
    }

    // Strategy 3.5: Remove malformed percent sequences before manual replacement
    try {
      let cleaned = encodedString
        // Remove malformed percent sequences (not followed by valid hex)
        .replace(/%[^0-9A-Fa-f]/g, '')
        .replace(/%[0-9A-Fa-f][^0-9A-Fa-f]/g, '')
        .replace(/\+/g, ' ')
        .replace(/%0A/g, '\n')
        .replace(/%0D/g, '\r')
        .replace(/%09/g, '\t')
        .replace(/%20/g, ' ')
        .replace(/%22/g, '"')
        .replace(/%27/g, "'")
        .replace(/%3C/g, '<')
        .replace(/%3E/g, '>')
        .replace(/%26/g, '&')
        .replace(/%3D/g, '=')
        .replace(/%2B/g, '+')
        .replace(/%2F/g, '/')
        .replace(/%3F/g, '?')
        .replace(/%23/g, '#')
        .replace(/%25/g, '%')
      
      return decodeURIComponent(cleaned)
    } catch (error) {
      console.warn('Malformed sequence removal decode failed:', error)
    }

    // Strategy 4: Progressive decode - try to decode in chunks
    try {
      return this._progressiveDecode(encodedString)
    } catch (error) {
      console.warn('Progressive decode failed:', error)
    }

    // Strategy 5: Complete manual decoding without decodeURIComponent
    try {
      return this._manualDecode(encodedString)
    } catch (error) {
      console.warn('Manual decode failed:', error)
    }

    // Strategy 6: Last resort - return with basic cleanup
    console.warn('All decode strategies failed, using basic cleanup')
    return encodedString.replace(/\+/g, ' ').replace(/%0A/g, '\n').replace(/%0D/g, '\r')
  }

  // Private: Try to decode URI component progressively
  _progressiveDecode(encodedString) {
    // First, clean up the string by removing malformed sequences
    let cleaned = encodedString
      .replace(/%[^0-9A-Fa-f]/g, '') // Remove % not followed by hex
      .replace(/%[0-9A-Fa-f][^0-9A-Fa-f]/g, '') // Remove incomplete sequences
    
    const parts = cleaned.split(/(%[0-9A-Fa-f]{2})/)
    let result = ''
    
    for (let i = 0; i < parts.length; i++) {
      const part = parts[i]
      if (part.match(/^%[0-9A-Fa-f]{2}$/)) {
        try {
          result += decodeURIComponent(part)
        } catch (error) {
          // If this specific part fails, use the raw part
          result += part
        }
      } else {
        result += part
      }
    }
    
    return result
  }

  // Private: Manual decoding without using decodeURIComponent
  _manualDecode(encodedString) {
    return encodedString
      .replace(/\+/g, ' ')
      .replace(/%20/g, ' ')
      .replace(/%0A/g, '\n')
      .replace(/%0D/g, '\r')
      .replace(/%09/g, '\t')
      .replace(/%22/g, '"')
      .replace(/%27/g, "'")
      .replace(/%3C/g, '<')
      .replace(/%3E/g, '>')
      .replace(/%26/g, '&')
      .replace(/%3D/g, '=')
      .replace(/%2B/g, '+')
      .replace(/%2F/g, '/')
      .replace(/%3F/g, '?')
      .replace(/%23/g, '#')
      .replace(/%25/g, '%')
      .replace(/%21/g, '!')
      .replace(/%40/g, '@')
      .replace(/%24/g, '$')
      .replace(/%5E/g, '^')
      .replace(/%28/g, '(')
      .replace(/%29/g, ')')
      .replace(/%5B/g, '[')
      .replace(/%5D/g, ']')
      .replace(/%7B/g, '{')
      .replace(/%7D/g, '}')
      .replace(/%7C/g, '|')
      .replace(/%5C/g, '\\')
      .replace(/%60/g, '`')
      .replace(/%7E/g, '~')
      .replace(/%2C/g, ',')
      .replace(/%3B/g, ';')
      .replace(/%3A/g, ':')
      .replace(/%2E/g, '.')
      .replace(/%2D/g, '-')
      .replace(/%5F/g, '_')
      .replace(/%3F/g, '?')
      .replace(/%2A/g, '*')
      .replace(/%2B/g, '+')
      .replace(/%2F/g, '/')
      .replace(/%3D/g, '=')
      .replace(/%26/g, '&')
      .replace(/%23/g, '#')
      .replace(/%25/g, '%')
      .replace(/%2B/g, '+')
      .replace(/%2F/g, '/')
      .replace(/%3F/g, '?')
      .replace(/%23/g, '#')
      .replace(/%25/g, '%')
      // Remove any remaining malformed percent sequences
      .replace(/%[^0-9A-Fa-f]/g, '')
      .replace(/%[0-9A-Fa-f][^0-9A-Fa-f]/g, '')
  }

  // Private: Populate input field with text, optional specialist slug and the
  // suggestion the text came from, which travels with the message so the sent
  // question can be attributed to the chip that produced it.
  _populateInput(text, specialistSlug = '', source = '') {
    fillAssistantComposer(text, { specialistSlug, source })
  }

}


